import { format } from 'date-fns';
import { clean } from 'diacritic';

import {
  Field,
  FirestoreSession,
  FilenameSettings,
  SessionPermissions,
  SessionUserPermission,
  FirestoreSessionPermission,
  SessionStep,
} from '../firestore';
import type { IRootStore } from './root';
import { BaseCard } from './card';
import { trimSpecialCharacters as trimSpecialCharactersFn } from '../trimSpecialCharacters';

export class BaseSession {
  protected rootStore: IRootStore;
  id: string;
  data: FirestoreSession;

  // Cache for user-specific permissions
  protected userPermissions: Record<string, FirestoreSessionPermission> = {};
  protected permissionsLoaded = false;

  constructor(id: string, data: FirestoreSession, rootStore: IRootStore) {
    this.data = data;
    this.id = id;
    this.rootStore = rootStore;
  }

  get modules() {
    return (this.data?.moduleIds ?? [])
      .map((moduleId) => this.data?.modules?.[moduleId])
      .filter(Boolean);
  }

  /**
   * Get effective visible fields for a module, handling backwards compatibility.
   * If legacy showThumbnails is true and attachmentFieldId exists but isn't in fields,
   * include it in the returned array.
   */
  getModuleVisibleFields(moduleId: string): string[] {
    const module = this.data?.modules?.[moduleId];
    if (!module) return [];

    const fields = [...module.fields];
    const source = this.rootStore.sourcesStore.sources[module.sourceId];

    // Backwards compat: if showThumbnails is true and attachmentFieldId not in fields, add it
    if (source?.data.attachmentFieldId && module.showThumbnails) {
      if (!fields.includes(source.data.attachmentFieldId)) {
        // Insert at beginning to match legacy behavior (thumbnails showed first)
        fields.unshift(source.data.attachmentFieldId);
      }
    }

    return fields;
  }

  /**
   * Check if a field should be displayed as an image in module view.
   * Returns true for: attachmentFieldId, imageFieldIds entries.
   */
  isImageField(sourceId: string, fieldId: string): boolean {
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) return false;

    // Check if it's the attachment field
    if (source.data.attachmentFieldId === fieldId) return true;

    // Check if it's an image column field
    if (source.data.imageFieldIds?.includes(fieldId)) return true;

    return false;
  }

  get cardList() {
    return (this.data.cardIds ?? [])
      .map((id) => this.rootStore.cardsStore.cards[id])
      .filter((s) => !!s);
  }

  get doneCardIds() {
    return (this.data.cardIds ?? [])
      .slice(0, this.currentCardIdx)
      .filter((id) => !!this.rootStore.cardsStore.cards[id])
      .reverse();
  }

  get inProgressCardIds() {
    return (this.data.cardIds ?? [])
      .slice(this.currentCardIdx)
      .filter((id) => !!this.rootStore.cardsStore.cards[id]);
  }

  get currentCardIdx() {
    const firstNotDone = this.cardList.findIndex((s) => !s.data.doneAt);
    return firstNotDone === -1 ? this.cardList.length : firstNotDone;
  }

  // ============================================
  // Multi-Step Queue Infrastructure
  // ============================================

  /**
   * Get the steps defined for this session (workflow stages)
   * Returns empty array if no steps configured (falls back to legacy binary queue)
   */
  get steps(): SessionStep[] {
    return this.data.steps ?? [];
  }

  /**
   * Check if this session has multi-step workflow enabled
   * When false, uses legacy binary queue (In Progress / Done)
   */
  get hasSteps(): boolean {
    return this.steps.length > 0;
  }

  /**
   * Get steps sorted by position
   */
  get sortedSteps(): SessionStep[] {
    return [...this.steps].sort((a, b) => a.position - b.position);
  }

  /**
   * Get cards filtered by step ID
   * @param stepId - The step ID to filter by, or null for "unassigned/triage" cards
   * @param excludeDone - If true, excludes cards with doneAt set (default: true)
   * @returns Array of card IDs in that step
   */
  getCardIdsByStepId(stepId: string | null, excludeDone: boolean = true): string[] {
    return (this.data.cardIds ?? []).filter((id) => {
      const card = this.rootStore.cardsStore.cards[id];
      if (!card) return false;

      // Exclude done cards unless explicitly requested
      if (excludeDone && card.data.doneAt) return false;

      // Match step (null matches cards with no stepId or stepId = null)
      if (stepId === null) {
        return card.data.stepId === null || card.data.stepId === undefined;
      }
      return card.data.stepId === stepId;
    });
  }

  /**
   * Get cards that are done (have doneAt set)
   * Step-aware: works regardless of whether steps are enabled
   */
  get doneCardIdsByStep(): string[] {
    return (this.data.cardIds ?? [])
      .filter((id) => {
        const card = this.rootStore.cardsStore.cards[id];
        return card && card.data.doneAt;
      })
      .reverse(); // Most recently done first
  }

  /**
   * Get all in-progress cards (not done), grouped by step
   * Returns a map of stepId -> cardIds
   */
  getInProgressCardsByStep(): Map<string | null, string[]> {
    const result = new Map<string | null, string[]>();

    // Initialize with null for unassigned cards
    result.set(null, []);

    // Initialize each step
    for (const step of this.steps) {
      result.set(step.id, []);
    }

    // Distribute cards to their steps
    for (const cardId of this.data.cardIds ?? []) {
      const card = this.rootStore.cardsStore.cards[cardId];
      if (!card || card.data.doneAt) continue;

      const stepId = card.data.stepId ?? null;
      const existing = result.get(stepId) ?? [];
      existing.push(cardId);
      result.set(stepId, existing);
    }

    return result;
  }

  /**
   * Load user-specific permissions for this session
   * This should be called when the session is loaded
   */
  async loadUserPermissions() {
    if (this.permissionsLoaded) return;

    try {
      // This will be implemented in the app-specific Session class
      // since it needs access to Firestore
      this.permissionsLoaded = true;
    } catch (error) {
      console.error('Failed to load user permissions:', error);
    }
  }

  /**
   * Get user-specific permission for a given user
   */
  getUserPermission(userId: string): SessionUserPermission | null {
    const permission = this.userPermissions[userId];
    return permission && permission.status ? permission.permission : null;
  }

  /**
   * Check if a user can view this session
   * Priority: User-specific permissions > Public access level
   */
  canUserViewSession(userId: string): boolean {
    // Check user-specific permissions first
    const userPermission = this.getUserPermission(userId);
    if (userPermission) {
      return (
        userPermission === SessionUserPermission.VIEW ||
        userPermission === SessionUserPermission.EDIT ||
        userPermission === SessionUserPermission.OWNER
      );
    }

    // Fall back to public access level
    return (
      this.data.publicAccessLevel === SessionPermissions.VIEW ||
      this.data.publicAccessLevel === SessionPermissions.EDIT
    );
  }

  /**
   * Check if a user can edit this session
   * Priority: User-specific permissions > Public access level
   */
  canUserEditSession(userId: string): boolean {
    // Check user-specific permissions first
    const userPermission = this.getUserPermission(userId);
    if (userPermission) {
      return (
        userPermission === SessionUserPermission.EDIT ||
        userPermission === SessionUserPermission.OWNER
      );
    }

    // Fall back to public access level
    return this.data.publicAccessLevel === SessionPermissions.EDIT;
  }

  /**
   * Check if a user is the owner of this session
   */
  isUserOwner(userId: string): boolean {
    return this.data.userId === userId;
  }

  /**
   * Legacy methods for backward compatibility
   * These should be updated to use the current user's ID
   */
  get canViewSession() {
    // This will be overridden in the app-specific Session class
    // to use the current user's ID
    return (
      this.data.publicAccessLevel === SessionPermissions.VIEW ||
      this.data.publicAccessLevel === SessionPermissions.EDIT
    );
  }

  get canEditSession() {
    // This will be overridden in the app-specific Session class
    // to use the current user's ID
    return this.data.publicAccessLevel === SessionPermissions.EDIT;
  }

  protected getModuleFieldValueForCard(field: Field, card: BaseCard) {
    const cardConnections = card.data.connections;
    const module = field.type === 'module' && this.data.modules[field.moduleId];
    if (!module) {
      return null;
    }

    const source = this.rootStore.sourcesStore.sources[module.sourceId];
    if (!source) {
      return null;
    }

    const rowId = cardConnections[module.id];
    if (typeof rowId !== 'string') {
      return null;
    }
    const row = source.rows[rowId];
    if (!row) {
      return null;
    }

    const part = row.getValue(field.field);
    return part || null;
  }

  computeFormulaField(field: Field, card: BaseCard, cardNumberWidth: number): string | null {
    let computedField: string | null = null;
    if (field.type === 'app') {
      if (field.field === 'SESSION_NAME') {
        computedField = this.data.name;
      } else if (field.field === 'POS_NO') {
        computedField = card.cardNumber.toString().padStart(cardNumberWidth, '0');
      } else if (field.field === 'CUSTOM_TXT') {
        computedField = field.text;
      } else if (field.field === 'CURR_DATE') {
        computedField = format(new Date(), field.format);
      } else if (field.field === 'FILENAME_SUFFIX') {
        computedField = card.suffix ?? null;
      }
    } else {
      computedField = this.getModuleFieldValueForCard(field, card);
    }
    return computedField;
  }

  /**
   * Get the effective card title formula for a given step.
   * Returns the step-specific formula if one is configured, otherwise falls back
   * to the session-global cardTitleFormula.
   */
  getEffectiveTitleFormula(stepId?: string | null): Field[] {
    if (stepId) {
      const stepFormula = this.data.stepLayoutOverrides?.[stepId]?.cardTitleFormula;
      if (stepFormula) return stepFormula;
    }
    return this.data.cardTitleFormula ?? [];
  }

  computeMainLabel(card: BaseCard, stepId?: string | null) {
    const errors: string[] = [];

    const titleFormula = this.getEffectiveTitleFormula(stepId);

    // Check if any module fields actually have values
    const hasAnyModuleValue = titleFormula.some((field) => {
      if (field.type !== 'module') return false;
      const value = this.getModuleFieldValueForCard(field, card);
      return value !== null;
    });

    // Check if there are any non-empty text fields
    const hasNonEmptyTextFields = titleFormula.some(
      (field) =>
        field.type === 'app' &&
        field.field === 'CUSTOM_TXT' &&
        field.text &&
        field.text.trim().length > 0,
    );

    // Check for other app fields that produce values (SESSION_NAME, POS_NO, etc)
    const hasOtherAppFields = titleFormula.some(
      (field) =>
        field.type === 'app' && field.field !== 'CUSTOM_TXT' && field.field !== 'FILENAME_SUFFIX',
    );

    // If no module values, no text content, and no other app fields, use manual title
    if (
      !hasAnyModuleValue &&
      !hasNonEmptyTextFields &&
      !hasOtherAppFields &&
      card.data.manualTitle
    ) {
      return {
        mainLabel: card.data.manualTitle,
        errors: [],
      };
    }

    let mainLabel = '';
    let pendingConnector = '';

    for (const field of titleFormula) {
      const computedField = this.computeFormulaField(field, card, 0);

      // CUSTOM_TXT acts as an inline separator between fields — append it directly
      // instead of pushing as a standalone part (which would get wrapped in ' – ')
      if (field.type === 'app' && field.field === 'CUSTOM_TXT') {
        pendingConnector += computedField ?? '';
        continue;
      }

      if (computedField === null && field.type === 'module') {
        errors.push(this.data.modules[field.moduleId]?.name);
      }

      const part = computedField ?? 'No Connection';
      if (!part) continue;

      if (mainLabel === '') {
        mainLabel = pendingConnector + part;
      } else {
        mainLabel += (pendingConnector || ' – ') + part;
      }
      pendingConnector = '';
    }

    if (pendingConnector && mainLabel) {
      mainLabel += pendingConnector;
    }

    return {
      mainLabel,
      // remove any duplicates if the same module is used multiple times
      errors: Array.from(new Set(errors)),
    };
  }

  safeFilenamePart = (
    originalName: string,
    {
      disableFilenameCleanup,
      disableSpecialCharacterCleanup,
      spaceSeparator,
      specialCharacterReplacement,
      trimSpaces,
      trimSpecialCharacters,
    }: FilenameSettings,
  ) => {
    let cleanedFilename = originalName;

    if (trimSpaces) {
      cleanedFilename = clean(cleanedFilename.trim());
    }

    if (trimSpecialCharacters) {
      cleanedFilename = trimSpecialCharactersFn(cleanedFilename);
    }

    if (!disableFilenameCleanup) {
      cleanedFilename = clean(cleanedFilename.trim()).replace(/\s+/g, spaceSeparator);

      if (spaceSeparator.length > 0) {
        cleanedFilename = cleanedFilename.replace(
          new RegExp(`${spaceSeparator}+`, 'g'),
          spaceSeparator,
        );
      }
    }

    if (!disableSpecialCharacterCleanup) {
      cleanedFilename = cleanedFilename.replace(
        new RegExp(`[^a-z0-9${spaceSeparator}]+`, 'gi'),
        specialCharacterReplacement,
      );
    }

    return cleanedFilename;
  };

  /**
   * This takes formula and settings as arguments instead of acccessing on instance so we can compute filename on the fly while editing the formula
   */
  computeFilename(card: BaseCard, filenameFormula: Field[], filenameSettings: FilenameSettings) {
    const errors: string[] = [];
    const filename = filenameFormula
      .reduce((parts: string[], field) => {
        const computedField = this.computeFormulaField(
          field,
          card,
          filenameSettings.cardNumberWidth,
        );
        if (computedField === null && field.type === 'module') {
          errors.push(this.data.modules[field.moduleId]?.name);
        }

        const part =
          filenameSettings.filenameCaps && computedField
            ? computedField.toUpperCase()
            : computedField;

        parts.push(part ?? 'No Connection');
        return parts;
      }, [])
      .map((f) => this.safeFilenamePart(f, filenameSettings))
      .join(filenameSettings.fieldSeparator);

    return {
      filename,
      // remove any duplicates if the same module is used multiple times
      errors: Array.from(new Set(errors)),
    };
  }

  getRowsForModule(moduleId: string, metadataRows: string[]) {
    const module = this.data.modules[moduleId];
    const sourceId = module?.sourceId;
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) {
      return [];
    }

    const rows = metadataRows.map((rowId) => source.rows[rowId]).filter((row) => !!row);

    // Reverse order for images and multi-text modules to show newest first
    if (source.type === 'image' || (source.type === 'text' && module.multi === true)) {
      rows.reverse();
    }

    return rows;
  }

  getModulesForSource = (sourceId: string) => this.modules.filter((m) => m.sourceId === sourceId);

  // Determines whether a given module is used for a formula
  getDependenciesForModule = (moduleId: string) => {
    const filenameDeps = (this.data?.filenameFormula ?? []).filter(
      (f) => f.type === 'module' && f.moduleId === moduleId,
    );
    const cardTitleDeps = (this.data?.cardTitleFormula ?? []).filter(
      (f) => f.type === 'module' && f.moduleId === moduleId,
    );
    return {
      cardTitleDeps,
      filenameDeps,
    };
  };

  getImageModules = () =>
    this.modules.filter((m) => {
      // Guard: a module's source can be undefined (orphaned module whose source
      // was deleted, or sources still mid-load). Treat it as not-an-image-module
      // rather than throwing — an unguarded `source.type` here blanks any surface
      // that calls this (e.g. the Whole View's group-by resolver).
      const source = this.rootStore.sourcesStore.sources[m.sourceId];
      return source?.type === 'image';
    });

  getDependenciesForSource = (sourceId: string) => {
    const sourceModules = this.getModulesForSource(sourceId);
    const sourceFilenameDeps = [];
    const sourceCardTitleDeps = [];

    for (const module of sourceModules) {
      const { filenameDeps, cardTitleDeps } = this.getDependenciesForModule(module.id);
      sourceFilenameDeps.push(filenameDeps);
      sourceCardTitleDeps.push(cardTitleDeps);
    }
    return { sourceFilenameDeps, sourceCardTitleDeps };
  };

  get hasInProgressCardsWithDeletedConnections() {
    return this.inProgressCardIds
      .map((cardId) => this.rootStore.cardsStore.cards[cardId])
      .some((card) => card.hasDeletedConnections);
  }

  get hasDoneCardsWithDeletedConnections() {
    return this.doneCardIds
      .map((cardId) => this.rootStore.cardsStore.cards[cardId])
      .some((card) => card.hasDeletedConnections);
  }
}
